/**
 * Tests host: the domain-specific Firestore writes injected into the shared
 * CreateFlow. A test is a url doc with a userTesting config; a figma test also
 * writes a pluginUserTesting bridge doc so the Figma plugin can find it.
 *
 * A test draft carries a userTesting object (the Piece 1 contract that keeps it
 * in the Tests list and out of the Sites list) and is not plugin-claimable: the
 * bridge doc is only written on finish. Tasks collected in the flow are optional
 * and edited later in Setup, so they can be empty here.
 */
import firebase from '../../firebase';
import { generateReadableSlug, isValidReadableSlug } from '../AeroSites/sitesUtils';
import { editFigmaUrl } from '../../utils/figmaEmbedUrl';
import { discardDraft } from '../../utils/drafts';
import { createTestCopy } from './testCopy';

const UT_DEFAULTS = {
  audioEnabled: true,
  videoEnabled: true,
  askDifficulty: false,
  welcomeMessage: "Thanks for helping test this design. Your feedback shapes what happens next.",
  thankYouMessage: 'Thank you for your feedback!',
};

function taskDocs(tasks) {
  return (tasks || [])
    .map((t) => (t || '').trim())
    .filter(Boolean)
    .map((title, i) => ({ id: `task-${i + 1}`, title, order: i + 1 }));
}

export function buildTestCreateActions(uid) {
  const db = firebase.firestore();
  const del = firebase.firestore.FieldValue.delete();
  const ts = () => firebase.firestore.FieldValue.serverTimestamp();
  const urlRef = (id) => db.collection('user').doc(uid).collection('url').doc(id);

  return {
    // A test draft carries a userTesting stub (enabled:false) so the Tests list
    // surfaces it and the Sites list does not. Readable slug id so a drop /
    // agent backed test serves at {slug}.aeroapp.ai once it goes live.
    async createDraft({ source, name, slug: suppliedSlug }) {
      const slug = (suppliedSlug && isValidReadableSlug(suppliedSlug))
        ? suppliedSlug
        : await generateReadableSlug(db);
      await urlRef(slug).set({
        userId: uid,
        brand: 'aero',
        generatedUrl: slug,
        title: (name || '').trim() || slug,
        draft: true,
        draftMeta: { source: source || null },
        userTesting: { enabled: false },
        createdAt: ts(),
      });
      return { urlId: slug };
    },

    async persistDraft({ urlId, meta }) {
      const patch = {
        draftMeta: {
          source: meta.source || null,
          figmaMode: meta.figmaMode || null,
          tasks: Array.isArray(meta.tasks) ? meta.tasks : [],
        },
        updatedAt: ts(),
      };
      if (meta.name) patch.title = meta.name;
      await urlRef(urlId).set(patch, { merge: true });
    },

    // Drop-backed test: deploySite (run by CreateFlow) set type:'html' +
    // activeVersion and cleared draft; enable a site-backed test pinned to the
    // version that just went live. No bridge doc (site-backed, not figma).
    async finishDrop({ urlId, deployResult, name, tasks }) {
      const patch = {
        draft: del,
        userTesting: {
          enabled: true,
          status: 'active',
          prototypeType: 'site',
          pinnedVersionId: deployResult.versionId,
          ...UT_DEFAULTS,
          tasks: taskDocs(tasks),
        },
        updatedAt: ts(),
      };
      if (name && name.trim()) patch.title = name.trim();
      await urlRef(urlId).set(patch, { merge: true });
      return { landing: `/user-testing/${urlId}/setup` };
    },

    // Figma test: store the render-ready embed url, enable the test, and write
    // the pluginUserTesting bridge so the Figma plugin can find it.
    async finishFigmaEmbed({ urlId, link, name, tasks }) {
      const prototypeUrl = editFigmaUrl((link || '').trim());
      const userTesting = {
        enabled: true,
        status: 'active',
        prototypeType: 'figma',
        prototypeUrl,
        ...UT_DEFAULTS,
        tasks: taskDocs(tasks),
      };
      const patch = { draft: del, userTesting, updatedAt: ts() };
      if (name && name.trim()) patch.title = name.trim();
      await urlRef(urlId).set(patch, { merge: true });
      await db.collection('pluginUserTesting').doc(urlId).set({
        figmaUrl: prototypeUrl,
        userTesting,
        source: 'dashboard',
        createdAt: ts(),
        ownerId: uid,
      });
      return { landing: `/user-testing/${urlId}/setup` };
    },

    // Published-site test: a test copy of the site (testCopy.js). The test
    // gets its own doc, slug and link, a copy of the site's live version
    // (same stored files, nothing uploaded), and the site is not written.
    // Every call makes a new copy, so a site can have several tests.
    async finishPublishedSite({ siteId, name, tasks, siteHost }) {
      const { landing } = await createTestCopy({ uid, siteId, name, tasks, siteHost });
      return { landing };
    },

    // Agent path: the agent deploys (deploy_site) then creates the test
    // (create_user_test) on the name-matched draft doc. Fire on the test being
    // configured (userTesting.enabled), which is the real "test created"
    // signal and lands after the deploy.
    watchLive({ urlId }, onLive) {
      let done = false;
      return urlRef(urlId).onSnapshot((snap) => {
        const d = snap.data();
        if (!done && d && d.userTesting && d.userTesting.enabled === true) {
          done = true;
          urlRef(urlId).set({ draft: del, updatedAt: ts() }, { merge: true });
          onLive(`/user-testing/${urlId}/setup`);
        }
      });
    },

    async discard({ urlId }) {
      await discardDraft({ uid, urlId });
    },
  };
}
